// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import {
  exceedsMaxCurrencyAmount,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { ChargeFormFields } from '@/project/components/charge-form-fields';
import {
  ChargeFormData,
  chargeFormSchema,
} from '@/project/types/charge-form-schema';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { MAX_DOLLAR_VALUE_LABEL } from '@/utilities/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { Paper } from '@mui/material';
import { useForm } from 'react-hook-form';

interface Props {
  pricingMode: string;
  // Line item quantity is stamped onto FIXED-mode charges as informational
  // context (not used for amount math). PER_UNIT charges still take quantity
  // from the form input. See billing-conceptual-model.md.
  lineItemQuantity: number;
  currencyCode: IsoCurrencyCode;
  // Parent line item's schedule type. Drives the trigger_type stamped on
  // the new charge: MILESTONE → MANUAL (won't auto-cron), PERIODIC →
  // TIME (cron-driven).
  isMilestone: boolean;
  onSave: (charge: ChargeFormValue) => void;
  onCancel: () => void;
}

export function ChargeAddForm(props: Props) {
  const form = useForm<ChargeFormData>({
    resolver: zodResolver(chargeFormSchema),
    defaultValues: {
      label: '',
      quantity: '1',
      unitPrice: { amount: '', isoCode: props.currencyCode },
      servicePeriodStart: null,
      servicePeriodEnd: null,
      expectedBillableDate: null,
    },
  });

  const handleSave = form.handleSubmit((data) => {
    const rawPrice = data.unitPrice.amount.trim();
    const qty =
      props.pricingMode === 'PER_UNIT'
        ? parseInt(data.quantity ?? '', 10) || 0
        : props.lineItemQuantity;
    const price = parseCurrencyAmount(rawPrice);
    const isQuantityInvalid = props.pricingMode === 'PER_UNIT' && qty <= 0;
    const isPriceMissing = rawPrice === '';
    const isPriceInvalid =
      isPriceMissing || price === null || price.isNegative();

    if (isQuantityInvalid) {
      form.setError('quantity', {
        type: 'client',
        message: 'Quantity must be at least 1',
      });
    }
    if (isPriceInvalid) {
      form.setError('unitPrice.amount', {
        type: 'client',
        message: isPriceMissing
          ? props.pricingMode === 'PER_UNIT'
            ? 'Unit price is required'
            : 'Amount is required'
          : props.pricingMode === 'PER_UNIT'
            ? 'Unit price cannot be negative'
            : 'Amount cannot be negative',
      });
    }
    // FIXED charges track total_price only (the form's "Amount" feeds total,
    // not unit_price). PER_UNIT charges carry both and must satisfy the BE's
    // unit_price × qty == total_price check.
    const isPerUnit = props.pricingMode === 'PER_UNIT';
    const total = price === null ? null : isPerUnit ? price.times(qty) : price;
    const isAmountOverBound =
      price !== null &&
      total !== null &&
      !isPriceInvalid &&
      (exceedsMaxCurrencyAmount(price) || exceedsMaxCurrencyAmount(total));
    if (isAmountOverBound) {
      form.setError('unitPrice.amount', {
        type: 'client',
        message: isPerUnit
          ? `Charge total (qty × unit price) must not exceed ${MAX_DOLLAR_VALUE_LABEL}`
          : `Amount must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      });
    }
    if (
      isQuantityInvalid ||
      isPriceInvalid ||
      isAmountOverBound ||
      price === null ||
      total === null
    ) {
      return;
    }

    props.onSave({
      id: `add-${String(Date.now())}`,
      key: null,
      label: data.label,
      pricingMode: props.pricingMode,
      quantity: qty,
      unitPrice: isPerUnit
        ? {
            amount: padAmountToMinorUnit(price, props.currencyCode),
            isoCode: props.currencyCode,
          }
        : null,
      totalPrice: {
        amount: padAmountToMinorUnit(total, props.currencyCode),
        isoCode: props.currencyCode,
      },
      servicePeriodStart: data.servicePeriodStart,
      servicePeriodEnd: data.servicePeriodEnd,
      expectedBillableDate: data.expectedBillableDate,
      billingEligibleDate: null,
      status: 'PLANNED',
      isCustomModified: true,
      // Stamp the trigger type from the parent line item's schedule —
      // milestone phase added inline must carry MANUAL so the cron skips
      // it and the listing renders the flag icon. Periodic adds are
      // TIME-triggered (cron flips them when the bill date passes).
      triggerType: props.isMilestone ? 'MANUAL' : 'TIME',
      // Client-only row; save rewrites sequence to array_index + 1.
      sequence: 0,
    });
  });

  return (
    <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
      <ChargeFormFields
        control={form.control}
        pricingMode={props.pricingMode}
        currencyCode={props.currencyCode}
        saveLabel="Add charge"
        onSave={handleSave}
        onCancel={props.onCancel}
      />
    </Paper>
  );
}
